import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import { Print } from '@mui/icons-material';
import api from '../../services/api';

const WorkIssueList = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const karigarParam = params.get('karigar') || '';
  const karatParam = params.get('karat') || '';

  const [karigar, setKarigar] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Karat order as specified
  const karatOrder = ['24K', '24/24K', '22/22K', '22K'];

  useEffect(() => {
    const fetchData = async () => {
      if (!karigarParam) {
        setError('Karigar parameter is required');
        setLoading(false);
        return;
      }

      try {
        // Fetch karigar details
        const karigarRes = await api.get('/api/karigar');
        const karigarData = karigarRes.data.find(k => k.abbreviation === karigarParam);
        setKarigar(karigarData);

        // Fetch work issued items
        const itemsRes = await api.get(`/api/karigar/work-issued?karigar=${karigarParam}`);
        let allItems = itemsRes.data || [];

        // Filter by karat if provided
        if (karatParam) {
          allItems = allItems.filter(item => item.karat === karatParam);
        }

        setItems(allItems);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.response?.data?.message || 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [karigarParam, karatParam]);

  const handlePrint = () => {
    window.print();
  };

  // Group items by karat and voucher number
  const groupedItems = {};
  items.forEach(item => {
    const key = `${item.karat}_${item.voucherNumber || 'N/A'}`;
    if (!groupedItems[key]) {
      groupedItems[key] = {
        karat: item.karat,
        voucherNumber: item.voucherNumber || 'N/A',
        issueDate: item.issueDate,
        items: []
      };
    }
    groupedItems[key].items.push(item);
  });

  // Sort by karat order
  const sortedGroups = Object.values(groupedItems).sort((a, b) => {
    const aIndex = karatOrder.indexOf(a.karat);
    const bIndex = karatOrder.indexOf(b.karat);
    return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Print Button - Hidden when printing */}
      <Box className="no-print" sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Print />} onClick={handlePrint}>
          Print
        </Button>
      </Box>

      {/* A4 Printable Content */}
      <Box sx={{
        width: '210mm',
        minHeight: '297mm',
        margin: '0 auto',
        padding: '20mm',
        backgroundColor: 'white',
        '@media print': {
          margin: 0,
          padding: '10mm',
        }
      }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, fontSize: '1.2rem' }}>
            Work Issue Voucher
          </Typography>
          {karigar && (
            <Box>
              <Typography variant="body1" sx={{ fontSize: '0.9rem' }}>
                Karigar: {karigar.abbreviation} - {karigar.name}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Tables grouped by Karat and Voucher Number */}
        {sortedGroups.map((group, groupIndex) => (
          <Box key={groupIndex} sx={{ mb: 2, pageBreakInside: 'avoid' }}>
            <Box sx={{ mb: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                Karat: {group.karat} | Voucher: {group.voucherNumber} | Issue Date: {group.issueDate ? new Date(group.issueDate).toLocaleDateString() : 'N/A'}
              </Typography>
            </Box>

            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', p: 0.5 }}>S.N.</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', p: 0.5 }}>Bill</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', p: 0.5 }}>Item</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', p: 0.5 }}>Qty</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', p: 0.5 }}>Wt(g)</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', p: 0.5 }}>Size</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', p: 0.5 }}>Desc</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', p: 0.5 }}>GSB</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', p: 0.5 }}>Net</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', p: 0.5 }}>Wastage%</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', p: 0.5 }}>Making</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {group.items.map((item, index) => {
                    const netWeight = (parseFloat(item.itemWeight) || 0) - (parseFloat(item.gsbWeight) || 0);
                    return (
                      <TableRow key={item._id}>
                        <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>{index + 1}</TableCell>
                        <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>{item.billName || 'N/A'} {item.billNumber || ''}</TableCell>
                        <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>{item.itemName || 'N/A'}</TableCell>
                        <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>{item.quantity || 0} {item.unit || 'Pcs'}</TableCell>
                        <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>{(parseFloat(item.itemWeight) || 0).toFixed(2)}</TableCell>
                        <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>{item.size || '-'}</TableCell>
                        <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>{item.description || '-'}</TableCell>
                        <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>{(parseFloat(item.gsbWeight) || 0).toFixed(2)}</TableCell>
                        <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>{netWeight.toFixed(2)}</TableCell>
                        <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>{item.wastage || '-'}</TableCell>
                        <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>{(parseFloat(item.makingCharge) || 0).toFixed(2)}</TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow>
                    <TableCell colSpan={4} sx={{ fontWeight: 700, textAlign: 'right', fontSize: '0.75rem', p: 0.5 }}>
                      Total:
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', p: 0.5 }}>
                      {group.items.reduce((sum, item) => sum + (parseFloat(item.itemWeight) || 0), 0).toFixed(2)}
                    </TableCell>
                    <TableCell colSpan={3}></TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', p: 0.5 }}>
                      {group.items.reduce((sum, item) => {
                        const net = (parseFloat(item.itemWeight) || 0) - (parseFloat(item.gsbWeight) || 0);
                        return sum + net;
                      }, 0).toFixed(2)}
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', p: 0.5 }}>
                      {group.items.reduce((sum, item) => sum + (parseFloat(item.makingCharge) || 0), 0).toFixed(2)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))}

        {items.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No work issued items found
            </Typography>
          </Box>
        )}
      </Box>

      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          @page {
            size: A4;
            margin: 10mm;
          }
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
        }
      `}</style>
    </Box>
  );
};

export default WorkIssueList;
